import { useMemo, useCallback, useRef, type KeyboardEvent } from 'react'
import { editorFontStyle } from './editor-constants'
import { highlightCode } from './highlight-code.mjs'
import './code-highlight.css'
export function CodeEditor({
  value,
  onChange,
  language,
  readOnly = false,
}: {
  value: string
  onChange: (v: string) => void
  language: string | null
  readOnly?: boolean
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const preRef = useRef<HTMLPreElement>(null)

  const highlighted = useMemo(() => highlightCode(value, language), [value, language])

  const handleScroll = useCallback(() => {
    if (preRef.current && textareaRef.current) {
      preRef.current.scrollTop = textareaRef.current.scrollTop
      preRef.current.scrollLeft = textareaRef.current.scrollLeft
    }
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Tab') {
        if (readOnly) return
        e.preventDefault()
        const ta = e.currentTarget
        const start = ta.selectionStart
        const end = ta.selectionEnd
        const next = value.substring(0, start) + '  ' + value.substring(end)
        onChange(next)
        requestAnimationFrame(() => {
          ta.selectionStart = ta.selectionEnd = start + 2
        })
      }
    },
    [value, onChange, readOnly],
  )

  return (
    <div className="file-code-editor relative h-full w-full overflow-hidden">
      <pre
        ref={preRef}
        className="absolute inset-0 m-0 p-4 overflow-auto pointer-events-none"
        style={editorFontStyle}
        aria-hidden
      >
        <code
          className={language ? `hljs language-${language}` : ''}
          dangerouslySetInnerHTML={{ __html: highlighted + '\n' }}
          style={{ background: 'transparent', padding: 0, display: 'block' }}
        />
      </pre>
      <textarea
        ref={textareaRef}
        value={value}
        readOnly={readOnly}
        aria-label={readOnly ? '文件内容（只读）' : '文件内容编辑器'}
        onChange={(e) => onChange(e.target.value)}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        wrap="off"
        className="absolute inset-0 w-full h-full resize-none p-4 bg-transparent outline-none border-0"
        style={{
          ...editorFontStyle,
          color: 'transparent',
          caretColor: 'var(--foreground)',
          WebkitTextFillColor: 'transparent',
        }}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
      />
    </div>
  )
}
